import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, Input, OnDestroy, ViewChild} from '@angular/core';
import {MatCard, MatCardContent, MatCardTitle} from '@angular/material/card';
import {Hold} from '@store/holds/holds.types';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {BranchSelectors} from '@store/branches/branch.selectors';
import {CoverImageGridComponent, MinimalCatalogRecord} from '@lib/components/cover-image-grid/cover-image-grid.component';
import {isDefined, isUndefined} from '@aviary/store/common/typing.helpers';
import {TruncateDirective} from '@aviary/directives/truncate.directive';
import {FormatPipe} from '@aviary/pipes/format.pipe';
import {HoldsSelectors} from '@store/holds/holds.selectors';
import {ActionButtonsComponent} from '@aviary/components/action-buttons/action-buttons.component';
import {MX_STANDARD_ANIMATION_DURATION_MILLISECONDS} from '@aviary/store/common/common.types';
import {SeeMoreLessButtonComponent} from '@lib/components/see-more-less-button/see-more-less-button.component';
import {NgTemplateOutlet} from '@angular/common';

const COLLAPSED_CONTAINER_HEIGHT: string = '16rem';

@Component({
  selector: 'px-ready-for-pickup',
  templateUrl: './ready-for-pickup.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCard, MatCardContent, MatCardTitle, SelectPipe, CoverImageGridComponent, TruncateDirective, FormatPipe, ActionButtonsComponent, SeeMoreLessButtonComponent, NgTemplateOutlet
  ]
})
export class ReadyForPickupComponent implements OnDestroy {

  @ViewChild('itemsContainer') public set itemsContainer(container: ElementRef<HTMLElement>) {
    this.itemsContainerElement = container?.nativeElement;
  }
  @Input() public forDependent: boolean = false;
  @Input() public isCard: boolean = false;

  public readonly readyForPickupSelectors = HoldsSelectors.ReadyForPickup;
  public readonly dependentReadyForPickupSelectors = HoldsSelectors.DependentReadyForPickup;
  public readonly branchesSelectors = BranchSelectors.List;
  public itemsAreExpanded: boolean = false;
  public titlesAreExpanded: boolean = false;
  public enableExpansionToggleButton: boolean = true;
  public itemsContainerHeight: string = COLLAPSED_CONTAINER_HEIGHT;

  private readonly changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);
  private itemsContainerElement?: HTMLElement;
  private pendingTransitionEndTimeoutId?: ReturnType<typeof setTimeout>;
  private pendingTransitionEndHandler?: (event: TransitionEvent) => void;

  public ngOnDestroy(): void {
    this.clearPendingTransitionEndHandlerFor(this.itemsContainerElement);
  }

  public minimalCatalogRecordsFor(holds: Hold[]): MinimalCatalogRecord<Hold>[] {
    return holds.map(hold => ({
      id: hold.catalogRecordId,
      imageUrl: hold.coverImageUrl,
      identifier: hold.catalogRecordIdentifier,
      extraData: hold
    }));
  }

  public onClickExpansionToggleButton(): void {
    if (this.itemsAreExpanded) {
      this.collapseItems();
    } else {
      this.expandItems();
    }
  }

  private expandItems(): void {
    const container = this.itemsContainerElement;
    if (isUndefined(container)) return;

    // Expand the titles first so that we can include them when measuring the full height of the container.
    this.updateDom(() => {
      this.enableExpansionToggleButton = false;
      this.titlesAreExpanded = true;
    });

    requestAnimationFrame(() => {
      // Measure the container and set the target height for expansion.
      this.updateDom(() => {
        const containerHeightWithExpandedTitles: number = container.scrollHeight;
        this.itemsContainerHeight = `${containerHeightWithExpandedTitles}px`;
      });

      const onEnd = (event: TransitionEvent) => {
        if (event.target !== container || event.propertyName !== 'height') return;
        this.clearPendingTransitionEndHandlerFor(container);

        requestAnimationFrame(() => {
          // Measure the container once more, just to make sure nothing is cut off due to possible measurement before title animation was complete.
          this.updateDom(() => {
            const containerHeightAfterContainerExpansion: number = container.scrollHeight;
            this.itemsContainerHeight = `${containerHeightAfterContainerExpansion}px`;
          });

          requestAnimationFrame(() => {
            this.setPostExpansionState();
          });
        });
      };

      this.pendingTransitionEndHandler = onEnd;
      container.addEventListener('transitionend', onEnd);

      // In case 'transitionend' never fires (perhaps the user has "prefers-reduced-motion" set, etc.), we need to get back into a good state.
      this.pendingTransitionEndTimeoutId = setTimeout(() => {
        if (isUndefined(this.pendingTransitionEndHandler)) return; // We've already done the post-transition work -- nothing to do here.

        this.clearPendingTransitionEndHandlerFor(container);
        this.setPostExpansionState();
      }, MX_STANDARD_ANIMATION_DURATION_MILLISECONDS + 100);  // Allow the expected transition to complete normally before coming to the rescue.
    });
  }

  private setPostExpansionState(): void {
    this.updateDom(() => {
      // Set the container back to 'auto' height so that we react correctly to page resizing.
      this.itemsContainerHeight = 'auto';
      this.itemsAreExpanded = true;
      this.enableExpansionToggleButton = true;
    });
  }

  private collapseItems(): void {
    const container = this.itemsContainerElement;
    if (isUndefined(container)) return;

    this.clearPendingTransitionEndHandlerFor(container); // In case of quick toggle button clicks, clear any expansion leftovers before we collapse.

    // Measure the container and replace 'auto' with this actual value to give us a real starting point.
    this.updateDom(() => {
      this.enableExpansionToggleButton = false;

      const currentContainerHeight: number = container.getBoundingClientRect().height;
      this.itemsContainerHeight = `${currentContainerHeight}px`;
    });

    requestAnimationFrame(() => {
      // Collapse the titles.
      this.updateDom(() => {
        this.titlesAreExpanded = false;
      });

      requestAnimationFrame(() => {
        // Collapse the container back to its original height.
        this.updateDom(() => {
          this.itemsContainerHeight = COLLAPSED_CONTAINER_HEIGHT;
          this.itemsAreExpanded = false;
          this.enableExpansionToggleButton = true;
        });
      });
    });
  }

  private updateDom(updater: () => void): void {
    updater();
    this.changeDetector.markForCheck();
  }

  private clearPendingTransitionEndHandlerFor(container?: HTMLElement): void {
    if (isDefined(this.pendingTransitionEndHandler)) {
      container?.removeEventListener('transitionend', this.pendingTransitionEndHandler);
      this.pendingTransitionEndHandler = undefined;
    }

    if (isDefined(this.pendingTransitionEndTimeoutId)) {
      clearTimeout(this.pendingTransitionEndTimeoutId);
      this.pendingTransitionEndTimeoutId = undefined;
    }
  }
}
